// src/pages/Home.jsx
import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { FaPlay, FaChevronRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, FreeMode } from 'swiper/modules';
import ContentSlider from '../components/ContentSlider';
import Header from '../components/Header';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import { getImageUrl } from '../utils/imageHelper';
import { getApiUrl, API_ENDPOINTS } from '../config/apiConfig';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';

const MOBILE_BREAKPOINT = 768;

const MiddleAdsContainer = styled.div`
  width: 100%;
  overflow: visible;
  padding: 0 5%;
  box-sizing: border-box;
  display: flex;
  gap: 20px;
  margin: 40px 0;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    margin: 30px 0;
  }
`;

const MiddleAdWrapper = styled.div`
  flex: 1;
  min-width: 300px;
  
  @media (max-width: 768px) {
    flex: 100%;
    min-width: 100%;
  }
`;

const HomeContainer = styled.div`
  width: 100%;
  position: relative;
  background-color: var(--background-start);
  min-height: 100vh;
  margin-left: 0;
  padding-left: 0;
  padding-bottom: ${props => props.$isMobile ? '80px' : '0'};
  box-sizing: border-box;
`;

const HeroWrapper = styled.div`
  width: 100%;
  height: ${props => props.$isMobile ? '70vh' : '90vh'};
  position: relative;
  overflow: hidden;
`;

const HeroSlide = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const BackdropImage = styled(motion.div)`
  position: absolute;
  top: -5%;
  left: 0;
  width: 100%;
  height: 110%;
  background-image: url(${props => props.$backdropUrl || '/placeholder-image.jpg'});
  background-size: cover;
  background-position: center 30%;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to top, rgba(10, 2, 28, 1) 20%, transparent 60%),
                linear-gradient(to right, rgba(10, 2, 28, 0.7) 20%, transparent 60%);
    z-index: 3;
  }
`;

const HeroContent = styled(motion.div)`
  position: absolute;
  z-index: 5;
  bottom: ${props => props.$isMobile ? '10%' : '15%'};
  left: ${props => props.$isMobile ? '5%' : '5%'};
  max-width: ${props => props.$isMobile ? '90%' : '45%'};
  
  @media (max-width: 480px) {
    left: 5%;
    bottom: 8%;
    max-width: 90%;
  }
`;

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const HeroTitle = styled.h1`
  font-size: ${props => props.$isMobile ? '2rem' : '3.5rem'};
  font-weight: 800;
  margin-bottom: ${props => props.$isMobile ? '0.5rem' : '1rem'};
  line-height: 1.1;
  text-shadow: 0 2px 20px #000;
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    margin-bottom: 0.3rem;
  }
`;

const HeroDescription = styled.p`
  font-size: ${props => props.$isMobile ? '0.8rem' : '1rem'};
  color: var(--secondary-color);
  margin-bottom: ${props => props.$isMobile ? '1rem' : '2rem'};
  text-shadow: 0 1px 10px #000;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    display: block !important;
  }
`;

const GradientButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: ${props => props.$isMobile ? '10px 20px' : '15px 30px'};
  border: none;
  border-radius: 3px;
  background: linear-gradient(90deg, var(--button-bg-start), var(--button-bg-end));
  color: white;
  font-size: ${props => props.$isMobile ? '0.9rem' : '1.1rem'};
  font-weight: 600;
  cursor: pointer !important;
  box-shadow: 0 0 20px rgba(255, 0, 127, 0.5);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(255, 0, 127, 0.7);
  }
`;

const ContentWrapper = styled.div`
  padding: ${props => props.$isMobile ? '20px 3% 20px 3%' : '40px 3% 30px 3%'};
  margin-left: 0;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 10;
`;

const ContentRow = styled.div`
  margin-bottom: ${props => props.$isMobile ? '30px' : '40px'};
`;

const RowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const RowTitle = styled.h2`
  font-size: ${props => props.$isMobile ? '1.3rem' : '2rem'};
  font-weight: 700;
  color: white;
  font-family: 'Bebas Neue', 'Lemon Milk', sans-serif;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    color: var(--gradient-cyan);
    
    svg {
      transform: translateX(3px);
    }
  }
`;

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const LoadingContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--background-start);
  z-index: 9999;
  font-size: 2rem;
  animation: ${pulse} 2s infinite ease-in-out;
`;

const EpisodeBox = styled(Link)`
  display: block;
  width: ${props => props.$isMobile ? '150px' : '280px'};
  height: ${props => props.$isMobile ? '220px' : '160px'};
  border-radius: 5px;
  overflow: hidden;
  position: relative;
  background: linear-gradient(135deg, #1a102a, #0b0614);
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  text-decoration: none;

  &:hover {
    transform: translateY(-5px);
    border-color: var(--sidebar-indicator-color);
    box-shadow: 0 10px 30px rgba(255, 0, 127, 0.3);
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.6);
      z-index: 1;
    }
  }
`;

const EpisodeImage = styled.div`
  width: 100%;
  height: ${props => props.$isMobile ? '120px' : '100%'};
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  background-color: ${props => props.image ? 'transparent' : 'var(--glass-bg)'};

  /* Fallback için */
  ${props => !props.image && `
    &::after {
      content: '${props.alt || 'Resim Yok'}';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #1a102a, #0b0614);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--secondary-color);
      font-size: 0.7rem;
      text-align: center;
      padding: 10px;
      box-sizing: border-box;
    }
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => props.$isMobile 
      ? 'linear-gradient(to bottom, transparent 40%, rgba(10, 2, 28, 0.95) 100%)' 
      : 'linear-gradient(to bottom, transparent 40%, rgba(10, 2, 28, 0.95) 100%)'};
    z-index: 1;
  }
`;

const TypeTag = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  color: var(--primary-color);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.5rem;
  font-weight: 600;
  z-index: 4;
  border: 1px solid var(--border-color);
`;

const EpisodeDate = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(10, 2, 28, 0.9);
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 600;
  color: var(--gradient-cyan);
  border: 1px solid var(--border-color);
  z-index: 4;
`;

const EpisodeInfo = styled.div`
  position: ${props => props.$isMobile ? 'relative' : 'absolute'};
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${props => props.$isMobile ? '10px 8px' : '8px'};
  color: white;
  z-index: 2;
  height: ${props => props.$isMobile ? 'auto' : 'auto'};
  background: ${props => props.$isMobile ? 'transparent' : 'transparent'};
`;

const EpisodeTitle = styled.h3`
  font-size: ${props => props.$isMobile ? '0.7rem' : '0.9rem'};
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EpisodeMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: ${props => props.$isMobile ? '0.6rem' : '0.75rem'};
  color: var(--secondary-color);
`;

const EpisodeSliderContainer = styled.div`
  position: relative;

  .swiper {
    overflow: visible; /* Hover efektlerinin kesilmemesi için */
  }

  .swiper-slide {
    width: auto;
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: #fff;
    background: rgba(15, 2, 38, 0.5);
    backdrop-filter: blur(5px);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    transition: opacity 0.2s ease;
    top: 50%;
    transform: translateY(-50%);
    
    &:after {
      font-size: 1rem;
    }

    &.swiper-button-disabled {
      opacity: 0;
      pointer-events: none;
    }
  }

  .swiper-button-prev {
    left: -20px;
  }
  .swiper-button-next {
    right: -20px;
  }
`;

const LoadingBar = styled.div`
  width: 120px;
  height: 4px;
  background: linear-gradient(90deg, #7b5cff, #9f7bff, #7b5cff);
  background-size: 200% 100%;
  border-radius: 10px;
  animation: loadingAnimation 1.2s infinite linear;

  @keyframes loadingAnimation {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [heroSlides, setHeroSlides] = useState([]);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [latestEpisodes, setLatestEpisodes] = useState([]);
  const [popularSeries, setPopularSeries] = useState([]);
  const [popularMovies, setPopularMovies] = useState([]);
  const [popularAnimes, setPopularAnimes] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [homeSettings, setHomeSettings] = useState({
    hero_content_ids: [],
    featured_series_ids: [],
    featured_movies_ids: [],
    featured_animes_ids: [],
    featured_series_title: 'Popüler Diziler',
    featured_movies_title: 'Popüler Filmler',
    featured_animes_title: 'Popüler Animeler',
    ad_home_top: '',
    ad_home_middle_1: '',
    ad_home_middle_2: '',
    ad_home_bottom: ''
  });

  const homeRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: homeRef, offset: ["start start", "end start"] });
  const backdropY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

// Tüm verileri yükle - DÜZELTİLMİŞ VERSİYON
useEffect(() => {
  const loadAllData = async () => {
    try {
      setIsLoading(true);
      
      // 1. Ayarları yükle (Doğru API endpoint'i)
      const settingsResponse = await fetch(getApiUrl(API_ENDPOINTS.GET_PUBLIC_SETTINGS));
      let settingsData = {};
      
      if (settingsResponse.ok) {
        settingsData = await settingsResponse.json();
        
        const processedSettings = {
          hero_content_ids: settingsData.hero_content_ids ? settingsData.hero_content_ids.split(',').filter(id => id.trim() !== '') : [],
          featured_series_ids: settingsData.featured_series_ids ? settingsData.featured_series_ids.split(',').filter(id => id.trim() !== '') : [],
          featured_movies_ids: settingsData.featured_movies_ids ? settingsData.featured_movies_ids.split(',').filter(id => id.trim() !== '') : [],
          featured_animes_ids: settingsData.featured_animes_ids ? settingsData.featured_animes_ids.split(',').filter(id => id.trim() !== '') : [],
          featured_series_title: settingsData.featured_series_title || 'Popüler Diziler',
          featured_movies_title: settingsData.featured_movies_title || 'Popüler Filmler',
          featured_animes_title: settingsData.featured_animes_title || 'Popüler Animeler',
          ad_home_top: settingsData.ad_home_top || '',
          ad_home_middle_1: settingsData.ad_home_middle_1 || '',
          ad_home_middle_2: settingsData.ad_home_middle_2 || '',
          ad_home_bottom: settingsData.ad_home_bottom || ''
        };
        
        setHomeSettings(processedSettings);

        // 2. Tüm içerikleri çek
        const contentResponse = await fetch(getApiUrl(API_ENDPOINTS.CHECK_CONTENT));
        if (!contentResponse.ok) throw new Error('İçerikler çekilemedi');
        
        const allContent = await contentResponse.json();

        // 3. Hero slider için içerikleri ayarla
        if (processedSettings.hero_content_ids.length > 0) {
          const selectedHeroSlides = allContent.filter(item => 
            processedSettings.hero_content_ids.includes(item.tmdb_id.toString()) && item.backdrop_path
          );
          setHeroSlides(selectedHeroSlides);
        } else {
          // Varsayılan: rastgele 5 içerik
          const randomHeroSlides = [...allContent]
            .sort(() => Math.random() - 0.5)
            .slice(0, 5)
            .filter(item => item.backdrop_path);
          setHeroSlides(randomHeroSlides);
        }

        // 4. Öne çıkan dizileri ayarla
        if (processedSettings.featured_series_ids.length > 0) {
          const featuredSeries = allContent.filter(item => 
            processedSettings.featured_series_ids.includes(item.tmdb_id.toString()) && item.media_type === 'tv'
          );
          setPopularSeries(featuredSeries);
        } else {
          // Varsayılan: popüler diziler
          const defaultSeries = allContent
            .filter(item => item.media_type === 'tv')
            .sort((a, b) => (b.vote_average || 0) - (a.vote_average || 0))
            .slice(0, 10);
          setPopularSeries(defaultSeries);
        }

        // 5. Öne çıkan filmleri ayarla
        if (processedSettings.featured_movies_ids.length > 0) {
          const featuredMovies = allContent.filter(item => 
            processedSettings.featured_movies_ids.includes(item.tmdb_id.toString()) && item.media_type === 'movie'
          );
          setPopularMovies(featuredMovies);
        } else {
          // Varsayılan: popüler filmler
          const defaultMovies = allContent
            .filter(item => item.media_type === 'movie')
            .sort((a, b) => (b.vote_average || 0) - (a.vote_average || 0))
            .slice(0, 10);
          setPopularMovies(defaultMovies);
        }

        // 6. Öne çıkan animeleri ayarla
        if (processedSettings.featured_animes_ids.length > 0) {
          const featuredAnimes = allContent.filter(item => 
            processedSettings.featured_animes_ids.includes(item.tmdb_id.toString()) && item.media_type === 'anime'
          );
          setPopularAnimes(featuredAnimes);
        } else {
          // Varsayılan: popüler animeler
          const defaultAnimes = allContent
            .filter(item => item.media_type === 'anime')
            .sort((a, b) => (b.vote_average || 0) - (a.vote_average || 0))
            .slice(0, 10);
          setPopularAnimes(defaultAnimes);
        }

        // 7. Son bölümleri çek
        const latestEpisodesData = await getAllEpisodesFromMySQL();
        setLatestEpisodes(latestEpisodesData.slice(0, 10));
      }

    } catch (error) {
      console.error("Veri çekerken hata:", error);
    } finally {
      setIsLoading(false);
    }
  };

  loadAllData();
}, [isMobile]); 


  // Slider otomatik geçiş
  useEffect(() => {
    if (heroSlides.length > 0) {
      const timer = setTimeout(() => {
        setCurrentHeroIndex(prev => (prev + 1) % heroSlides.length);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [currentHeroIndex, heroSlides.length]);

  const getAllEpisodesFromMySQL = async () => {
    try {
      const response = await fetch(`${getApiUrl(API_ENDPOINTS.GET_EPISODES)}?limit=50`);
      
      if (!response.ok) {
        throw new Error('Bölümler çekilemedi');
      }
      
      const data = await response.json();
      
      if (data.success && data.episodes) {
        const processedEpisodes = data.episodes.map(episode => {
          const episodeDate = episode.air_date ? new Date(episode.air_date) : 
                            episode.created_at ? new Date(episode.created_at) : new Date();
          
          return {
            tmdb_id: episode.tmdb_id,
            title: episode.content_title,
            media_type: episode.media_type,
            backdrop_path: episode.backdrop_path,
            poster_path: episode.poster_path,
            vote_average: episode.vote_average,
            season: episode.season_number,
            episode: episode.episode_number,
            episodeData: episode,
            episodeDate: episodeDate,
            contentId: episode.content_id,
            episodeTitle: episode.episode_title,
            episodeOverview: episode.episode_overview,
            thumbnailPath: episode.thumbnail_path,
            airDate: episode.air_date
          };
        });
        
        return processedEpisodes;
      } else {
        throw new Error(data.error || 'Geçersiz veri formatı');
      }
      
    } catch (error) {
      console.error("Bölüm çekerken hata:", error);
      return [];
    }
  };
  
  const slideVariants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 },
  };

const getCardData = (item) => {
  const typeMap = { movie: 'Film', tv: 'Dizi', anime: 'Anime' };

  let rating = item.vote_average;
  if (typeof rating === 'string') rating = parseFloat(rating);
  if (isNaN(rating)) rating = 0;

  // poster_path zaten proxy URL ise tekrar sarmıyoruz
  const imageUrl = item.poster_path?.includes('/proxy-image.php')
    ? item.poster_path
    : getImageUrl(item.poster_path);

  return {
    id: item.tmdb_id, 
    title: item.title,
    mediaType: item.media_type,
    imageUrl: imageUrl,
    rating: rating,
    releaseDate: item.release_date,
    mediaTypeLabel: typeMap[item.media_type] || 'İçerik'
  };
};

const getEpisodeCardData = (episodeItem) => {
  const typeMap = { movie: 'Film', tv: 'Dizi', anime: 'Anime' };

  const episodeDate = episodeItem.episodeDate;
  let formattedDate = 'Yeni';
  if (episodeDate && episodeDate instanceof Date && !isNaN(episodeDate)) {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (episodeDate.getFullYear() < today.getFullYear()) {
      formattedDate = `${episodeDate.getDate()} ${episodeDate.toLocaleDateString('tr-TR', { month: 'long' })} ${episodeDate.getFullYear()}`;
    } else if (episodeDate.toDateString() === today.toDateString()) {
      formattedDate = 'Bugün';
    } else if (episodeDate.toDateString() === yesterday.toDateString()) {
      formattedDate = 'Dün';
    } else {
      formattedDate = `${episodeDate.getDate()} ${episodeDate.toLocaleDateString('tr-TR', { month: 'short' })}`;
    }
  }

  // thumbnail > backdrop > poster sırası ile proxy URL oluştur
  const imageUrlCandidate = episodeItem.thumbnailPath || episodeItem.backdrop_path || episodeItem.poster_path;
  const imageUrl = imageUrlCandidate?.includes('/proxy-image.php')
    ? imageUrlCandidate
    : getImageUrl(imageUrlCandidate);

  return {
    id: episodeItem.tmdb_id,
    contentId: episodeItem.contentId,
    title: episodeItem.title,
    mediaType: episodeItem.media_type,
    imageUrl: imageUrl,
    backdropUrl: episodeItem.backdrop_path ? getImageUrl(episodeItem.backdrop_path) : getImageUrl(episodeItem.poster_path),
    rating: episodeItem.vote_average || 0,
    season: episodeItem.season,
    episode: episodeItem.episode,
    episodeTitle: episodeItem.episodeTitle || `${episodeItem.season}.Sezon ${episodeItem.episode}.Bölüm`,
    episodeDate: formattedDate,
    mediaTypeLabel: typeMap[episodeItem.media_type] || 'İçerik'
  };
};

  const EpisodeCard = ({ item }) => {
    const watchUrl = item.mediaType === 'movie'
      ? `/movie/${item.id}`
      : `/watch/${item.mediaType === 'anime' ? 'tv' : item.mediaType}/${item.id}/season/${item.season}/episode/${item.episode}`;

    return (
      <EpisodeBox to={watchUrl} $isMobile={isMobile}>
        <EpisodeImage image={item.imageUrl} $isMobile={isMobile} />
        <TypeTag>
          {item.mediaTypeLabel}
        </TypeTag>
        <EpisodeDate>{item.episodeDate}</EpisodeDate>
        <EpisodeInfo $isMobile={isMobile}>
          <EpisodeTitle $isMobile={isMobile}>{item.title}</EpisodeTitle>
          <EpisodeMeta>
            <span>
              {item.mediaType === 'movie' ? 'Film İzle' : `${item.season}.Sezon ${item.episode}.Bölüm`}
            </span>
          </EpisodeMeta>
        </EpisodeInfo>
      </EpisodeBox>
    );
  };

  const EpisodeSlider = ({ items, isMobile }) => {
    const displayItems = items.slice(0, 10);
    
    return (
      <EpisodeSliderContainer>
        <Swiper
          modules={[Navigation, FreeMode]}
          spaceBetween={15}
          slidesPerView={'auto'}
          freeMode={true}
          navigation={!isMobile}
        >
          {displayItems.map((item, index) => (
            <SwiperSlide key={`${item.contentId}-${item.season}-${item.episode}-${index}`}>
              <EpisodeCard item={item} />
            </SwiperSlide>
          ))}
        </Swiper>
      </EpisodeSliderContainer>
    );
  };

  const currentSlide = heroSlides[currentHeroIndex];

  return (
    <HomeContainer ref={homeRef} $isMobile={isMobile}>
      <SEO />
      <AnimatePresence>
        {isLoading && (
          <LoadingContainer
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div
              style={{
                textAlign: 'center',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <LoadingBar />
              <p
                style={{
                  color: '#b9aaff',
                  fontSize: '1.1rem',
                  marginTop: '15px',
                }}
              >
                İçerikler yükleniyor...
              </p>
            </div>
          </LoadingContainer>
        )}
      </AnimatePresence>

      
      {!isLoading && (
        <>
          <Header />
          
          {/* Reklam - Üst */}
          {homeSettings.ad_home_top && (
            <div style={{ 
              padding: '20px 5%', 
              background: 'var(--background-start)', 
              width: '100%', 
              overflow: 'visible',
              boxSizing: 'border-box',
              position: 'relative',
              zIndex: 1
            }}>
              <AdBanner 
                adCode={homeSettings.ad_home_top}
                height="120px"
                mobileHeight="100px"
                margin="0"
                mobileMargin="0"
                showLabel={false}
                bgColor="transparent"
                border="none"
                showBox={false}
                overflow="visible"
              />
            </div>
          )}

          <HeroWrapper 
            $isMobile={isMobile}
          >
            <AnimatePresence initial={false}>
              {currentSlide && (
                <HeroSlide key={currentHeroIndex}>
                  <BackdropImage 
                    $backdropUrl={currentSlide.backdrop_path ? getImageUrl(currentSlide.backdrop_path, 'original') : '/placeholder-image.jpg'}
                    style={{ y: backdropY }}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ opacity: { duration: 0.7 } }}
                  />
                  <HeroContent
                      initial={{ opacity: 0, x: -50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 1, delay: 0.5 }}
                      $isMobile={isMobile}
                  >
                    <HeroTitle $isMobile={isMobile}>{currentSlide.title}</HeroTitle>
                    <HeroDescription $isMobile={isMobile}>
                      {currentSlide.overview?.substring(0, isMobile ? 300 : 500) || 'Açıklama bulunmuyor...'}
                    </HeroDescription>
                    <ButtonWrapper>
                      <Link to={`/${currentSlide.media_type}/${currentSlide.tmdb_id}`}>
                        <GradientButton $isMobile={isMobile}>
                          <FaPlay /> İncele
                        </GradientButton>
                      </Link>
                    </ButtonWrapper>
                  </HeroContent>
                </HeroSlide>
              )}
            </AnimatePresence>
          </HeroWrapper>
          
          <ContentWrapper $isMobile={isMobile}>
            {/* Son Gelen Bölümler */}
            <ContentRow $isMobile={isMobile}>
              <RowHeader>
                <RowTitle 
                  $isMobile={isMobile} 
                  onClick={() => window.location.href = '/tum-bolumler'}
                >
                  Son Gelen Bölümler
                  <FaChevronRight />
                </RowTitle>
              </RowHeader>
              {latestEpisodes.length > 0 ? (
                <EpisodeSlider items={latestEpisodes.map(item => getEpisodeCardData(item))} isMobile={isMobile} />
              ) : (
                <div style={{ color: 'var(--secondary-color)', textAlign: 'center', padding: '20px' }}>
                  Henüz bölüm eklenmemiş
                </div>
              )}
            </ContentRow>

            {/* Reklam - Orta (Yan Yana 2 Reklam) */}
            {(homeSettings.ad_home_middle_1 || homeSettings.ad_home_middle_2) && (
              <MiddleAdsContainer>
                {homeSettings.ad_home_middle_1 && (
                  <MiddleAdWrapper>
                    <AdBanner 
                      adCode={homeSettings.ad_home_middle_1}
                      height="250px"
                      mobileHeight="200px"
                      margin="0"
                      mobileMargin="0"
                      showLabel={false}
                      bgColor="transparent"
                      border="none"
                      showBox={false}
                      overflow="visible"
                    />
                  </MiddleAdWrapper>
                )}
                {homeSettings.ad_home_middle_2 && (
                  <MiddleAdWrapper>
                    <AdBanner 
                      adCode={homeSettings.ad_home_middle_2}
                      height="250px"
                      mobileHeight="200px"
                      margin="0"
                      mobileMargin="0"
                      showLabel={false}
                      bgColor="transparent"
                      border="none"
                      showBox={false}
                      overflow="visible"
                    />
                  </MiddleAdWrapper>
                )}
              </MiddleAdsContainer>
            )}

            {/* Öne Çıkan Diziler */}
            {popularSeries.length > 0 && (
              <ContentRow $isMobile={isMobile}>
                <RowHeader>
                  <RowTitle 
                    $isMobile={isMobile} 
                    onClick={() => window.location.href = '/kesfet'}
                  >
                    {homeSettings.featured_series_title}
                    <FaChevronRight />
                  </RowTitle>
                </RowHeader>
                <ContentSlider items={popularSeries.map(item => getCardData(item))} isMobile={isMobile} />
              </ContentRow>
            )}

            {/* Öne Çıkan Filmler */}
            {popularMovies.length > 0 && (
              <ContentRow $isMobile={isMobile}>
                <RowHeader>
                  <RowTitle 
                    $isMobile={isMobile} 
                    onClick={() => window.location.href = '/kesfet'}
                  >
                    {homeSettings.featured_movies_title}
                    <FaChevronRight />
                  </RowTitle>
                </RowHeader>
                <ContentSlider items={popularMovies.map(item => getCardData(item))} isMobile={isMobile} />
              </ContentRow>
            )}

            {/* Öne Çıkan Animeler */}
            {popularAnimes.length > 0 && (
              <ContentRow $isMobile={isMobile}>
                <RowHeader>
                  <RowTitle 
                    $isMobile={isMobile} 
                    onClick={() => window.location.href = '/kesfet'}
                  >
                    {homeSettings.featured_animes_title}
                    <FaChevronRight />
                  </RowTitle>
                </RowHeader>
                <ContentSlider items={popularAnimes.map(item => getCardData(item))} isMobile={isMobile} />
              </ContentRow>
            )}

            {/* Reklam - Alt */}
            {homeSettings.ad_home_bottom && (
              <div style={{ width: '100%', overflow: 'visible', padding: '0 5%', boxSizing: 'border-box' }}>
                <AdBanner 
                  adCode={homeSettings.ad_home_bottom}
                  height="250px"
                  mobileHeight="200px"
                  margin="40px 0 0 0"
                  mobileMargin="30px 0 0 0"
                  showLabel={false}
                  bgColor="transparent"
                  border="none"
                  showBox={false}
                  overflow="visible"
                />
              </div>
            )}
          </ContentWrapper>
        </>
      )}
    </HomeContainer>
  );
};

export default Home;
